import type { PageContext } from 'vike/types';

// Helpers for the per-route +title.ts / +description.ts / +image.ts
// files under pages/. Centralized so every route uses the same site
// name resolution + og:image fallback chain — drift between routes
// is what produced "The Herd on Nonprofit Trips" on a ScoreMore URL.

const FALLBACK_SITE_NAME = 'Nonprofit Trips';

interface SitePageContext {
   site?: {
      displayName?: string;
      visualBranding?: {
         primaryLogo?: string | null;
      };
   };
}

/**
 * Resolves the human-readable site name from the request-scoped
 * siteConfig. Falls back to "Nonprofit Trips" only if site resolution
 * failed entirely (Host header didn't match a registered tenant), which
 * shouldn't happen in normal operation.
 */
export function getSiteName(pageContext: PageContext): string {
   const site = (pageContext as unknown as SitePageContext).site;
   return site?.displayName?.trim() || FALLBACK_SITE_NAME;
}

/**
 * Resolves an og:image URL with a tenant-aware fallback chain:
 *   1. The page-specific image (org logo, hotel hero, event banner, …)
 *   2. The site's primaryLogo from siteConfig.visualBranding
 *   3. undefined — vike-react omits the og:image / twitter:image tags
 *
 * Most pages had no fallback before this, so any page with missing
 * page-specific data emitted no og:image at all and produced unbranded
 * unfurls. This guarantees every page has at least the tenant's brand
 * mark in the share preview.
 */
export function resolveOgImage(
   pageContext: PageContext,
   primary: string | undefined | null,
): string | undefined {
   const trimmed = primary?.trim();
   if (trimmed) return trimmed;
   const site = (pageContext as unknown as SitePageContext).site;
   const fallback = site?.visualBranding?.primaryLogo?.trim();
   return fallback || undefined;
}
